import { Card } from '@/components/ui/Card';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import type { EstimateHeader, EstimateStatus } from '../../shared/types/estimate.types';

interface EstimatesGridProps {
  items: EstimateHeader[];
  isLoading: boolean;
  error: Error | null;
  onItemClick: (id: string) => void;
}

const statusMeta: Record<EstimateStatus, { label: string; variant: BadgeProps['variant'] }> = {
  draft: { label: 'Nháp', variant: 'neutral' },
  reviewing: { label: 'Đang duyệt', variant: 'warning' },
  approved: { label: 'Đã duyệt', variant: 'success' },
  locked: { label: 'Đã khóa', variant: 'info' },
  converted_to_boq: { label: 'Đã chuyển BOQ', variant: 'purple' },
  revised: { label: 'Điều chỉnh', variant: 'blue' },
  cancelled: { label: 'Đã hủy', variant: 'danger' },
};

function formatCurrency(value: number) {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(value || 0);
}

export function EstimatesGrid({ items, isLoading, error, onItemClick }: EstimatesGridProps) {
  if (error) {
    return (
      <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Không tải được danh sách dự toán: {error.message}
      </Card>
    );
  }

  if (isLoading) {
    return <Card className="border-slate-200 bg-white p-6 text-sm text-slate-500">Đang tải danh sách dự toán...</Card>;
  }

  if (items.length === 0) {
    return (
      <Card className="border-slate-200 bg-white p-6 text-sm text-slate-500">
        Chưa có hồ sơ dự toán phù hợp với bộ lọc hiện tại.
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {items.map((estimate) => {
        const meta = statusMeta[estimate.status_code] || statusMeta.draft;
        return (
          <Card
            key={estimate.id}
            className="cursor-pointer border-slate-200 bg-white p-4 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 flex flex-col h-full"
            onClick={() => onItemClick(estimate.id)}
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <Badge variant="info">{estimate.estimate_code}</Badge>
              <Badge variant={meta.variant}>{meta.label}</Badge>
            </div>
            <h3 className="mb-1 line-clamp-2 text-sm sm:text-base font-medium tracking-wide text-slate-900 transition-colors hover:text-emerald-700">
              {estimate.estimate_name}
            </h3>
            <p className="mb-4 flex items-center gap-1.5 text-xs sm:text-sm text-slate-500">
              <i className="ri-building-4-line text-sm" />
              {estimate.customer_name || estimate.project?.name || 'Chưa gắn khách hàng'}
            </p>

            <div className="flex items-center justify-between border-t border-slate-100 pt-3 mt-auto">
              <span className="rounded-md bg-slate-50 px-2 py-1 text-[10px] sm:text-xs font-medium text-slate-500">
                {estimate.estimate_type === 'project' ? 'Dự án' : 'Báo giá lẻ'}
              </span>
              <span className="text-sm sm:text-base font-bold text-slate-900 tracking-tight">{formatCurrency(estimate.total_amount)}</span>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
